import { DOCUMENT } from '@angular/common';
import { Injectable, NgZone, effect, inject } from '@angular/core';
import { RendererFactory2 } from '@angular/core';

import { ScrollProxyService } from '@frontend/sports/common/core/utils/scroll-service-proxy';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { ViewTransitionUtils, WINDOW } from '@frontend/vanilla/core';

import { ScrollBlockObserverService } from './scroll-block-observer.service';
import { ScrollToTopObserverService } from './scroll-to-top-observer.service';

/** TODO: move remaining layout reads (window.scrollY, slotHeaderBottomItemsOffset) into the IntersectionObserver */

/**
 * Hide the header slightly before reaching the offset point since the transition takes time
 * and it should't cover page content for any longer than absolutely necessary.
 */
const HIDE_HEADER_OFFSET_FRACTION = 0.9;

@Injectable({
    providedIn: 'root',
})
export class HideHeaderService {
    #isAtTop = true;
    #isEnabled = true;
    #isFrozen = false;
    #isScheduled = false;
    #lastScrollPosition = 0;
    #isStyleOptimizationInitialized = false;
    #slotHeader: WeakRef<HTMLElement> | null = null;
    #slotHeaderBottomItems: WeakRef<HTMLElement> | null = null;

    readonly #minScrollChangeThreshold = 5;

    readonly #doc = inject(DOCUMENT);
    readonly #window = inject(WINDOW);
    readonly #ngZone = inject(NgZone);
    readonly #scrollProxy = inject(ScrollProxyService);
    readonly #sportsProductUtils = inject(SportsProductUtils);
    readonly #scrollBlockObserver = inject(ScrollBlockObserverService);
    readonly #scrollToTopObserver = inject(ScrollToTopObserverService);
    readonly #renderer = inject(RendererFactory2).createRenderer(null, null);
    readonly #viewTransitionUtils = inject(ViewTransitionUtils);

    scrollProxyConsumer = this.#scrollProxy.onScroll$.subscribe(() => this.onScroll());

    private get slotHeader(): HTMLElement | null {
        const element = this.getCachedElementOrRequery(this.#slotHeader, '.slot-header');

        this.#slotHeader = element ? new WeakRef(element) : null;

        return element;
    }

    private get slotHeaderBottomItems(): HTMLElement | null {
        const element = this.getCachedElementOrRequery(this.#slotHeaderBottomItems, '.slot-header_bottom_items');

        this.#slotHeaderBottomItems = element ? new WeakRef(element) : null;

        return element;
    }

    private get slotHeaderBottomItemsOffset(): number {
        return this.slotHeaderBottomItems?.offsetTop ?? 0;
    }

    // Consider moving to vanilla lib in future to replace the sports/casino versions.
    constructor() {
        this.initialize();

        effect(() => this.handleIsScrolledTop());
        effect(() => this.handleIsScrollBlocked());

        this.#sportsProductUtils
            .fromSportsEnter()
            .pipe(this.#viewTransitionUtils.deferUntilTransitionReady())
            .subscribe(() => this.initialize());

        this.#sportsProductUtils
            .fromSportsLeave()
            .pipe(this.#viewTransitionUtils.deferUntilTransitionReady())
            .subscribe(() => this.uninitialize());
    }

    onScroll(): void {
        if (!this.#isEnabled) {
            return;
        }

        if (this.#isScheduled || this.#isFrozen) {
            return;
        }

        if (!this.#isStyleOptimizationInitialized) {
            this.initializeStyleOptimization();
        }

        this.scheduleHandleScroll();
    }

    freeze(): void {
        this.#isFrozen = true;
    }

    release(): void {
        this.#isFrozen = false;
    }

    private handleIsScrolledTop(): void {
        this.#isAtTop = this.#scrollToTopObserver.isAtTop();

        if (this.#isAtTop) {
            this.showHeader();

            if (this.slotHeader) {
                this.#renderer.addClass(this.slotHeader, 'no-transition');
            }
        } else {
            if (this.slotHeader) {
                this.#renderer.removeClass(this.slotHeader, 'no-transition');
            }
        }
    }

    private handleIsScrollBlocked(): void {
        if (this.#scrollBlockObserver.isBlocked()) {
            this.freeze();
        } else {
            this.release();
        }
    }

    private initialize(): void {
        this.#isEnabled = true;

        // temporary - necessary to override the last top value set by the casino header visibility styles service
        if (this.slotHeader) {
            this.#renderer.setStyle(this.slotHeader, 'top', '0');
        }
    }

    private uninitialize(): void {
        this.#isEnabled = false;

        if (this.slotHeader) {
            this.#renderer.removeStyle(this.slotHeader, 'translate');
            this.#renderer.removeClass(this.slotHeader, 'optimise');
            this.#renderer.removeClass(this.slotHeader, 'no-transition');
        }
    }

    private initializeStyleOptimization(): void {
        if (this.slotHeader) {
            this.#renderer.addClass(this.slotHeader, 'optimise');
            this.#isStyleOptimizationInitialized = true;
        }
    }

    private scheduleHandleScroll(): void {
        this.#isScheduled = true;

        this.#ngZone.runOutsideAngular(() => {
            this.#window.requestAnimationFrame(() => this.handleScroll());
        });
    }

    private handleScroll(): void {
        this.#isScheduled = false;

        const { currentPosition, delta } = this.updateScrollPosition();

        if (this.#isAtTop) {
            this.showHeader();

            return;
        }

        if (this.isAtOffset(currentPosition)) {
            this.showHeader();

            return;
        }

        if (this.isTinyScrollMovement(delta)) {
            return;
        }

        if (this.isScrollingDown(delta)) {
            this.hideHeader(this.slotHeaderBottomItemsOffset);
        } else {
            this.showHeader();
        }
    }

    private updateScrollPosition(): { currentPosition: number; delta: number } {
        const currentPosition = this.#window.scrollY;
        const delta = currentPosition - this.#lastScrollPosition;

        this.#lastScrollPosition = currentPosition;

        return { currentPosition, delta };
    }

    private isAtOffset(currentPosition: number): boolean {
        return currentPosition <= this.slotHeaderBottomItemsOffset * HIDE_HEADER_OFFSET_FRACTION;
    }

    private isTinyScrollMovement(delta: number): boolean {
        return Math.abs(delta) <= this.#minScrollChangeThreshold;
    }

    private isScrollingDown(delta: number): boolean {
        return delta > 0;
    }

    private hideHeader(offset: number): void {
        if (this.slotHeader) {
            this.#renderer.setStyle(this.slotHeader, 'translate', `0 ${-offset}px 0`);
        }
    }

    private showHeader(): void {
        if (this.slotHeader) {
            this.#renderer.setStyle(this.slotHeader, 'translate', `0 0 0`);
        }
    }

    private getCachedElementOrRequery(ref: WeakRef<HTMLElement> | null, selector: string): HTMLElement | null {
        const element = ref?.deref();

        if (element?.isConnected) {
            return element;
        }

        return this.#doc.querySelector<HTMLElement>(selector);
    }
}
